import React, { useState, useEffect } from 'react';
import { AiOutlineCheck } from 'react-icons/ai';
import SlidePanel from './slide-panel';

/**
 * Collection Parameters Component
 * 
 * A multi-select component for configuring BGG collection parameters.
 * Allows users to select multiple collection filters and parameters.
 */
const CollectionParameters = ({ 
    isOpen, 
    onClose, 
    onApply, 
    currentParams = {} 
}) => {
    // State for all collection parameters
    const [params, setParams] = useState({
        // Ownership filters
        own: currentParams.own ?? true,
        rated: currentParams.rated ?? false,
        played: currentParams.played ?? false,
        comment: currentParams.comment ?? false,
        trade: currentParams.trade ?? true,
        want: currentParams.want ?? false,
        wishlist: currentParams.wishlist ?? false,
        wishlistpriority: currentParams.wishlistpriority ?? null,
        preordered: currentParams.preordered ?? false,
        wanttoplay: currentParams.wanttoplay ?? false,
        wanttobuy: currentParams.wanttobuy ?? false,
        prevowned: currentParams.prevowned ?? false,
        hasparts: currentParams.hasparts ?? false,
        wantparts: currentParams.wantparts ?? false,
        
        // Rating filters
        minrating: currentParams.minrating ?? null,
        rating: currentParams.rating ?? null,
        minbggrating: currentParams.minbggrating ?? null,
        bggrating: currentParams.bggrating ?? null,
        
        // Play count filters
        minplays: currentParams.minplays ?? null,
        maxplays: currentParams.maxplays ?? null,
        
        // Other filters
        subtype: currentParams.subtype ?? 'boardgame',
        excludesubtype: currentParams.excludesubtype ?? 'boardgameaccessory',
        brief: currentParams.brief ?? false,
        stats: currentParams.stats ?? false,
        showprivate: currentParams.showprivate ?? false,
        version: currentParams.version ?? true
    });

    // Reset state when panel opens
    useEffect(() => {
        if (isOpen) {
            setParams({
                own: currentParams.own ?? true,
                rated: currentParams.rated ?? false,
                played: currentParams.played ?? false,
                comment: currentParams.comment ?? false,
                trade: currentParams.trade ?? true,
                want: currentParams.want ?? false,
                wishlist: currentParams.wishlist ?? false,
                wishlistpriority: currentParams.wishlistpriority ?? null,
                preordered: currentParams.preordered ?? false,
                wanttoplay: currentParams.wanttoplay ?? false,
                wanttobuy: currentParams.wanttobuy ?? false,
                prevowned: currentParams.prevowned ?? false,
                hasparts: currentParams.hasparts ?? false,
                wantparts: currentParams.wantparts ?? false,
                minrating: currentParams.minrating ?? null,
                rating: currentParams.rating ?? null,
                minbggrating: currentParams.minbggrating ?? null,
                bggrating: currentParams.bggrating ?? null,
                minplays: currentParams.minplays ?? null,
                maxplays: currentParams.maxplays ?? null,
                subtype: currentParams.subtype ?? 'boardgame',
                excludesubtype: currentParams.excludesubtype ?? 'boardgameaccessory',
                brief: currentParams.brief ?? false,
                stats: currentParams.stats ?? false,
                showprivate: currentParams.showprivate ?? false,
                version: currentParams.version ?? true
            });
        }
    }, [isOpen, currentParams]);

    const handleCheckboxChange = (key) => {
        setParams(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleNumberChange = (key, value) => {
        const numValue = value === '' ? null : parseInt(value);
        setParams(prev => ({
            ...prev,
            [key]: numValue
        }));
    };

    const handleSelectChange = (key, value) => {
        setParams(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleApply = () => {
        onApply(params);
        onClose();
    };

    const handleReset = () => {
        setParams({
            own: true,
            rated: false,
            played: false,
            comment: false,
            trade: true,
            want: false,
            wishlist: false,
            wishlistpriority: null,
            preordered: false,
            wanttoplay: false,
            wanttobuy: false,
            prevowned: false,
            hasparts: false,
            wantparts: false,
            minrating: null,
            rating: null,
            minbggrating: null,
            bggrating: null,
            minplays: null,
            maxplays: null,
            subtype: 'boardgame',
            excludesubtype: 'boardgameaccessory',
            brief: false,
            stats: false,
            showprivate: false,
            version: true
        });
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        
        // Count boolean filters that are true
        const booleanFilters = [
            'own', 'rated', 'played', 'comment', 'trade', 'want', 'wishlist',
            'preordered', 'wanttoplay', 'wanttobuy', 'prevowned', 'hasparts', 'wantparts',
            'brief', 'stats', 'showprivate', 'version'
        ];
        
        booleanFilters.forEach(key => {
            if (params[key]) count++;
        });

        // Count numeric filters that have values
        const numericFilters = [
            'wishlistpriority', 'minrating', 'rating', 'minbggrating', 'bggrating',
            'minplays', 'maxplays'
        ];
        
        numericFilters.forEach(key => {
            if (params[key] !== null && params[key] !== '') count++;
        });

        // Count select filters that aren't defaults
        if (params.subtype !== 'boardgame') count++;
        if (params.excludesubtype !== 'boardgameaccessory') count++;

        return count;
    };

    const activeFiltersCount = getActiveFiltersCount();

    return (
        <SlidePanel
            isOpen={isOpen}
            onClose={onClose}
            title={`Collection Parameters ${activeFiltersCount > 0 ? `(${activeFiltersCount})` : ''}`}
            width="420px"
            footer={
                <>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                        <button 
                            onClick={onClose}
                            className="control-button secondary-button"
                            style={{ flex: 1 }}
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleReset}
                            className="control-button secondary-button"
                            style={{ flex: 1}}
                        >
                            Defaults
                        </button>
                        <button 
                            onClick={handleApply}
                            className="control-button primary-button"
                            style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                        >
                            <AiOutlineCheck />
                            Apply Filters
                        </button>
                    </div>
                </>
            }
        >
            <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border-light)' }}>
                    <label style={{ fontWeight: '500', marginBottom: '12px', display: 'block' }}>Game Type</label>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '0.8rem', color: 'var(--color-text-light)', display: 'block', marginBottom: '4px' }}>
                                Include subtype
                            </label>
                            <select
                                value={params.subtype}
                                onChange={(e) => handleSelectChange('subtype', e.target.value)}
                                className="control-input"
                                style={{ width: '100%' }}
                            >
                                <option value="boardgame">Board Game</option>
                                <option value="boardgameexpansion">Expansion</option>
                                <option value="boardgameaccessory">Accessory</option>
                                <option value="rpgitem">RPG Item</option>
                                <option value="rpgissue">RPG Issue</option>
                                <option value="videogame">Video Game</option>
                            </select>
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '0.8rem', color: 'var(--color-text-light)', display: 'block', marginBottom: '4px' }}>
                                Exclude subtype
                            </label>
                            <select
                                value={params.excludesubtype}
                                onChange={(e) => handleSelectChange('excludesubtype', e.target.value)}
                                className="control-input"
                                style={{ width: '100%' }}
                            >
                                <option value="">None</option>
                                <option value="boardgameaccessory">Board Game Accessory</option>
                                <option value="boardgameexpansion">Board Game Expansion</option>
                                <option value="boardgame">Board Game</option>
                                <option value="rpgitem">RPG Item</option>
                                <option value="rpgissue">RPG Issue</option>
                                <option value="videogame">Video Game</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Ownership Status Filters */}
                <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border-light)' }}>
                    <label style={{ fontWeight: '500', marginBottom: '12px', display: 'block' }}>Ownership Status</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.own}
                                onChange={() => handleCheckboxChange('own')}
                            />
                            <span>Owned</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.trade}
                                onChange={() => handleCheckboxChange('trade')}
                            />
                            <span>For Trade</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.wishlist}
                                onChange={() => handleCheckboxChange('wishlist')}
                            />
                            <span>Wishlist</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.want}
                                onChange={() => handleCheckboxChange('want')}
                            />
                            <span>Want (Trade)</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.wanttoplay}
                                onChange={() => handleCheckboxChange('wanttoplay')}
                            />
                            <span>Want to Play</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.wanttobuy}
                                onChange={() => handleCheckboxChange('wanttobuy')}
                            />
                            <span>Want to Buy</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.prevowned}
                                onChange={() => handleCheckboxChange('prevowned')}
                            />
                            <span>Previously Owned</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.preordered}
                                onChange={() => handleCheckboxChange('preordered')}
                            />
                            <span>Pre-ordered</span>
                        </label>
                    </div>
                </div>

                {/* Interaction Filters */}
                <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border-light)' }}>
                    <label style={{ fontWeight: '500', marginBottom: '12px', display: 'block' }}>Interaction Status</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.rated}
                                onChange={() => handleCheckboxChange('rated')}
                            />
                            <span>Rated</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.played}
                                onChange={() => handleCheckboxChange('played')}
                            />
                            <span>Played</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.comment}
                                onChange={() => handleCheckboxChange('comment')}
                            />
                            <span>Commented</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.hasparts}
                                onChange={() => handleCheckboxChange('hasparts')}
                            />
                            <span>Has Parts</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.wantparts}
                                onChange={() => handleCheckboxChange('wantparts')}
                            />
                            <span>Wants Parts</span>
                        </label>
                    </div>
                </div>

                {/* Wishlist Priority */}
                <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border-light)' }}>
                    <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Wishlist Priority</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {[1, 2, 3, 4, 5].map(priority => (
                            <label key={priority} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', textAlign: 'left' }}>
                                <input
                                    type="radio"
                                    name="wishlistpriority"
                                    value={priority}
                                    checked={params.wishlistpriority === priority}
                                    onChange={(e) => handleNumberChange('wishlistpriority', e.target.value)}
                                />
                                <span>Priority {priority}</span>
                            </label>
                        ))}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="wishlistpriority"
                                value=""
                                checked={params.wishlistpriority === null}
                                onChange={(e) => handleNumberChange('wishlistpriority', e.target.value)}
                            />
                            <span>Any</span>
                        </label>
                    </div>
                </div>

                {/* Rating Filters */}
                <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border-light)' }}>
                    <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Rating Filters</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div>
                            <label style={{ fontSize: '0.8rem', color: 'var(--color-text-light)', display: 'block', marginBottom: '4px' }}>
                                Min Personal Rating
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={params.minrating || ''}
                                onChange={(e) => handleNumberChange('minrating', e.target.value)}
                                className="control-input"
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.8rem', color: 'var(--color-text-light)', display: 'block', marginBottom: '4px' }}>
                                Max Personal Rating
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={params.rating || ''}
                                onChange={(e) => handleNumberChange('rating', e.target.value)}
                                className="control-input"
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.8rem', color: 'var(--color-text-light)', display: 'block', marginBottom: '4px' }}>
                                Min BGG Rating
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={params.minbggrating || ''}
                                onChange={(e) => handleNumberChange('minbggrating', e.target.value)}
                                className="control-input"
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.8rem', color: 'var(--color-text-light)', display: 'block', marginBottom: '4px' }}>
                                Max BGG Rating
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={params.bggrating || ''}
                                onChange={(e) => handleNumberChange('bggrating', e.target.value)}
                                className="control-input"
                            />
                        </div>
                    </div>
                </div>

                {/* Play Count Filters */}
                <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border-light)' }}>
                    <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Play Count Filters</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div>
                            <label style={{ fontSize: '0.8rem', color: 'var(--color-text-light)', display: 'block', marginBottom: '4px' }}>
                                Min Plays
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="9999"
                                value={params.minplays || ''}
                                onChange={(e) => handleNumberChange('minplays', e.target.value)}
                                className="control-input"
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.8rem', color: 'var(--color-text-light)', display: 'block', marginBottom: '4px' }}>
                                Max Plays
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="9999"
                                value={params.maxplays || ''}
                                onChange={(e) => handleNumberChange('maxplays', e.target.value)}
                                className="control-input"
                            />
                        </div>
                    </div>
                </div>

                {/* Additional Options */}
                <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border-light)' }}>
                    <label style={{ fontWeight: '500', marginBottom: '12px', display: 'block' }}>Additional Options</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.version}
                                onChange={() => handleCheckboxChange('version')}
                            />
                            <span>Include Version Info</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.stats}
                                onChange={() => handleCheckboxChange('stats')}
                            />
                            <span>Include Stats</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.brief}
                                onChange={() => handleCheckboxChange('brief')}
                            />
                            <span>Brief Results</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={params.showprivate}
                                onChange={() => handleCheckboxChange('showprivate')}
                            />
                            <span>Show Private Info</span>
                        </label>
                    </div>
                </div>
            </SlidePanel>
    );
};

export default CollectionParameters;